import React from 'react';
import {
  Paper,
  Typography,
  Tooltip,
  Box,
  Button,
  // Chip, // Chip can be removed if not used elsewhere
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
// Removed import { styled } from '@mui/material/styles'; as it might not be needed if no other styled components exist
import { DataGrid, GridColDef, GridRowsProp, GridRenderCellParams } from '@mui/x-data-grid'; // Import DataGrid
import { COLOR_GREY_600, COLOR_GREY_800, COLOR_PRIMARY_2, COLOR_TEXT_GREY } from 'src/colors';
import { useUpgradeMesageExpertFeature } from 'src/hooks/useUpgradeText';
import { convertCurrency } from 'src/utils/functions';
import { exchangeRates } from 'src/const';

// Define blur states (copied from MarketExplorerPage for encapsulation)
type BlurState = 'none' | 'blurMarket' | 'blurAll';

// Define the CSS styles for blurring
const blurStyle = {
  filter: 'blur(4px)',
  userSelect: 'none',
  pointerEvents: 'none',
} as const; // Use 'as const' for stricter typing of style keys

// Define the structure for metadata (Import or define if not already)
interface ColumnMetadata {
  unit: string | null;
  is_currency: boolean;
  display_name: string;
  is_sortable: boolean;
  data_type: string;
  width?: number;
}

interface MarketFinderMetadata {
  [key: string]: ColumnMetadata;
}

// Define the structure for the data rows (Adjust based on DUMMY_MARKET_FINDER_DATA)
export interface MarketDataRow {
  rank: number;
  name: string;
  short_term_rental_yield?: number;
  rent_gap?: number;
  occupancy_rate?: number;
  adr?: number;
  revenue?: number;
  supply?: number;
  regulation?: string; // Or appropriate type
  yoy_supply_change?: number;
  yoy_revenue_change?: number;
  [key: string]: any; // Allow other properties
}

// Ensure StyledTableCell, StyledHeaderCell, DataTableCell, and related comments are completely removed.

// Tooltip mapping based on your initial request
const columnTooltips: { [key: string]: string } = {
  short_term_rental_yield:
    'Ratio of annual Airbnb income to property price. Higher yield means better ROI.',
  rent_gap:
    'Difference between long-term and short-term rental returns. Larger gaps often mean higher Airbnb profit potential.',
  revenue: 'Median annual revenue generated by 2-bedroom listings in this market.',
  regulation:
    "This is based on the percentage of listings that include license details. While it's a useful signal, it may not always reflect actual regulation strictness or enforcement.",
  yoy_supply_change:
    'Year-over-year change in the number of active listings. Indicates market saturation or growth.',
  yoy_revenue_change:
    'Year-over-year change in median listing revenue. Helps spot improving or declining markets.',
};

// Tooltip helper component
const HeaderWithTooltip = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <Box display="flex" alignItems="center" gap={0.5} sx={{ paddingRight: '30px' }}>
    {/* Added bold font weight directly here */}
    <Typography variant="inherit" component="span" sx={{ fontWeight: 'bold' }}>
      {label}
    </Typography>
    <Tooltip title={tooltip} arrow placement="top">
      <InfoOutlinedIcon sx={{ fontSize: '1rem', color: COLOR_TEXT_GREY }} />
    </Tooltip>
  </Box>
);

// Helper function to format cell values for DataGrid
const formatCellValue = (
  value: any,
  metadata: ColumnMetadata | undefined,
  currency: string,
  currencySymbol: string
): string => {
  if (value === null || value === undefined || value === '-') return '-';

  let formattedValue: string | number = value;
  let isCurrencyField = metadata?.is_currency;

  // Format numbers with locale string and handle percentages/currency
  if (metadata && (metadata.data_type === 'number' || metadata.data_type === 'integer')) {
    try {
      const numberValue = Number(value);
      if (!isNaN(numberValue)) {
        // Convert currency first if applicable
        const valueToFormat = isCurrencyField
          ? convertCurrency(currency, numberValue)
          : numberValue;

        formattedValue = valueToFormat.toLocaleString(undefined, {
          // Use minimumFractionDigits for currency, allowing decimals
          minimumFractionDigits: isCurrencyField ? 2 : metadata.unit === '%' ? 1 : 0,
          maximumFractionDigits: isCurrencyField ? 2 : metadata.unit === '%' ? 1 : 0,
        });
      } else {
        formattedValue = '-'; // Handle non-numeric gracefully
      }
    } catch (e) {
      formattedValue = '-'; // Handle errors during conversion
    }
  } else if (typeof value === 'number') {
    try {
      // Format standalone numbers like 'rank'
      formattedValue = value.toLocaleString();
    } catch (e) {
      formattedValue = String(value); // Fallback if toLocaleString fails
    }
  } else {
    formattedValue = String(value); // Ensure string conversion for non-numeric types
  }

  // Apply currency/percentage symbols AFTER formatting
  if (isCurrencyField) {
    // Use the passed currencySymbol
    return `${currencySymbol}${formattedValue}`;
  } else if (metadata?.unit === '%') {
    return `${formattedValue}%`;
  }
  // Regulation or other specific string formatting could be added here if needed
  // Example: if (metadata?.display_name === 'Regulations') return specificFormat(value);

  return String(formattedValue); // Ensure final return is a string
};

// Apply blur based on state and column
const CellContent = ({
  params,
  columnMeta,
  currency,
  currencySymbol,
}: {
  params: GridRenderCellParams;
  columnMeta?: ColumnMetadata;
  currency: string;
  currencySymbol: string;
}) => {
  const value = params.value;
  const blurState = params.row._blurState as 'none' | 'blurMarket' | 'blurAll';
  const field = params.field;

  let applyBlur = false;
  // Apply blur logic, explicitly exclude the 'rank' column
  if (field !== 'rank') {
    if (blurState === 'blurAll') {
      applyBlur = true;
    } else if (blurState === 'blurMarket') {
      // Check for 'blurMarket' state (ranks 1-3)
      // Blur name and supply for ranks 1-3 (yoy_supply_change was removed from blur)
      if (field === 'name' || field === 'supply') {
        applyBlur = true;
      }
    }
  }

  // Check for 'locked' value specifically BEFORE formatting
  const isLockedValue = value === 'locked';
  // Pass currency and symbol to formatCellValue only if not 'locked'
  const formattedValue = isLockedValue ? 'locked' : formatCellValue(value, columnMeta, currency, currencySymbol);

  // Special handling for 'name' column to allow wrapping
  if (field === 'name') {
    return (
      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'normal',
          lineHeight: 'normal',
          ...(applyBlur && blurStyle), // Apply blur style conditionally
        }}
      >
        {formattedValue}
      </Typography>
    );
  }

  // Default rendering for other cells, apply blur style if needed
  return <span style={applyBlur ? blurStyle : {}}>{formattedValue}</span>;
};

const BlurCTA = ({handleOnSampleClick, totalRows}: {handleOnSampleClick; totalRows: number}) => {
  const upgradeMessage = useUpgradeMesageExpertFeature();
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
        position: 'absolute',
        top: `${Math.min(totalRows, 3) * 67}px`,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        height: '150px',
        backgroundColor: '#fff0',
        zIndex: 1000,
        padding: '20px',
        borderRadius: '12px',
        pointerEvents: 'none',
      }}
    >
      <Button
        variant="contained"
        onClick={() => handleOnSampleClick('expert')}
        sx={{ color: '#fff' }}
        startIcon={<LockOpenOutlinedIcon />}
      >
        {upgradeMessage}
      </Button>
    </Box>
  );
};

const MarketDataGrid = ({
  data,
  metadata,
  isFreeUser,
  handleOnSampleClick,
  currency
}: {
  data: MarketDataRow[];
  metadata: MarketFinderMetadata;
  isFreeUser: boolean;
  handleOnSampleClick;
  currency: string;
}) => {
  const metadataKeys = Object.keys(metadata);
  const currencySymbol = exchangeRates[currency].symbol;

  // Define columns for DataGrid
  const columns: GridColDef[] = [
    {
      field: 'rank',
      headerName: 'Rank',
      renderHeader: () => (
        <Typography variant="inherit" component="span" sx={{ fontWeight: 'bold' }}>
          Rank
        </Typography>
      ),
      width: 100,
      sortable: false,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => <CellContent params={params} currency={currency} currencySymbol={currencySymbol}/>,
    },
    {
      field: 'name',
      headerName: 'Name',
      renderHeader: () => (
        <Typography variant="inherit" component="span" sx={{ fontWeight: 'bold' }}>
          Name
        </Typography>
      ),
      width: 250, // Adjust width as needed
      sortable: false,
      renderCell: (params: GridRenderCellParams) => <CellContent params={params} currency={currency} currencySymbol={currencySymbol}/>,
      headerAlign: 'left',
      align: 'left',
      type: 'string',
    },
    // Dynamically create columns from metadata
    ...metadataKeys.map(
      (key): GridColDef => {
        const columnMeta = metadata[key];
        const tooltipText = columnTooltips[key];
        return {
          field: key,
          headerName: columnMeta.display_name, // Use display_name for underlying header name (sorting/filtering)
          renderHeader: () =>
            tooltipText ? (
              <HeaderWithTooltip label={columnMeta.display_name} tooltip={tooltipText} />
            ) : (
              <Typography
                variant="inherit"
                component="span"
                sx={{ fontWeight: 'bold', paddingRight: '30px' }}
              >
                {columnMeta.display_name}
              </Typography>
            ),
          width: columnMeta.width ?? 170, // Use width from metadata if available, otherwise default to 170
          sortable: false,
          renderCell: (params: GridRenderCellParams) => (
            <CellContent params={params} columnMeta={columnMeta} currency={currency} currencySymbol={currencySymbol}/>
          ),
          headerAlign: 'left',
          align: 'left',
          // Add type for potential filtering/sorting improvements
          type:
            (columnMeta.data_type === 'number' || columnMeta.data_type === 'integer')
              ? 'number'
              : 'string',
        };
      }
    ),
  ];

  // Prepare rows for DataGrid, ensuring each has a unique 'id'
  const rows: GridRowsProp = data.map((row, index) => ({
    id: row.rank ?? `row-${index}`, // Use rank as ID, fallback to index + prefix for uniqueness
    ...row,
  }));

  return (
    <Box
      component={Paper}
      elevation={0}
      sx={{
        position: 'relative',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        mt: 2,
        width: '100%',
        height: 'auto',
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight
        disableSelectionOnClick
        hideFooterPagination
        hideFooterSelectedRowCount
        density="standard"
        disableColumnMenu
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: COLOR_GREY_800 + '88',
            borderBottom: '1px solid ' + COLOR_GREY_600,
            color: 'text.primary',
          },
          // Ensure header titles are bold (already handled by renderHeader, but good fallback)
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          // Ensure header text alignment
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            justifyContent: 'flex-start',
            padding: 0,
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #e0e0e0',
            padding: '12px 16px',
            color: 'text.primary',
            // verticalAlign: 'top',
            lineHeight: 'normal',
          },
          '& .MuiDataGrid-row': {
            backgroundColor: '#ffffff',
            '&:hover': {
              backgroundColor: '#f9f9f9',
            },
            // Remove bottom border from the last row
            //  '&:last-child .MuiDataGrid-cell': {
            //      borderBottom: 'none',
            //  },
          },
          // Style the virtual scroller if height is constrained
          '& .MuiDataGrid-virtualScroller': {
            // maxHeight: '450px',
            overflowY: 'auto',
          },
          // Remove focus outline on cells
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          // Remove focus outline on column headers
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
        }}
      />

      {isFreeUser && <BlurCTA handleOnSampleClick={handleOnSampleClick} totalRows={rows ? rows.length : 1} />}
    </Box>
  );
};

export default MarketDataGrid;
